import { useContext, useState } from 'react';
import NextLink from 'next/link';
import { Flipped } from 'react-flip-toolkit';
import { CbAreYouSureModal } from 'ui/components/CbAreYouSureModal';
import { NewsfeedItem } from 'types/NewsfeedItem';
import styles from './CbNewsfeedItem.module.css';
import buttonStyles from '../Buttons/CbButton/CbButton.module.css';
import { Box, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { AdditionalFeed, NewsfeedContext } from 'providers';
import { useNewsfeedItem, useRelativeTime } from 'hooks';
import { CbFileDisplay } from './CbFileDisplay';

// @ts-ignore
import Microlink from '@microlink/react';
import linkifyStr from 'linkify-string';
import DOMPurify from 'dompurify';
import { CbAvatar } from '../CbAvatar';
import CbReactions from '../CbReactions/CbReactions';
import { Comments } from '../Comments';
import { CbButton, CbOptionsButton } from '../Buttons';
import { CbMultiSelect } from '../CbMultiSelect';
enum ItemActions {
  Repost = 'Repost',
  Delete = 'Delete Post',
}
export const CbNewsfeedItem = ({
  item,
  isManager,
  deletePost
}: {
  item: NewsfeedItem;
  isManager: boolean;
  deletePost: (id: number) => Promise<void>;
}) => {
  const {
    isOpen: isDeleteOpen,
    onClose: onDeleteClose,
    onOpen: onDeleteOpen
  } = useDisclosure();
  const {
    isOpen: isRepostOpen,
    onClose: onRepostClose,
    onOpen: onRepostOpen
  } = useDisclosure();
  const {
    id,
    text,
    reactions,
    author,
    files,
    imageUrl,
    urlForPreview,
    links,
    topComment,
    commentsCount,
    createdAt
  } = item;
  const itemAge = useRelativeTime(createdAt);
  const imageUrlFile = {
    name: 'image',
    type: 'image',
    url: imageUrl,
    id: 0
  };
  const displayedFiles = [...files];

  // Backwards compatability for old newsfeed items. This should be removed once the feature flag is removed for all users and all newsfeed items have been updated.
  if (imageUrl) {
    displayedFiles.unshift(imageUrlFile);
  }

  // Don't show links to the Newsfeed on the Newsfeed
  const displayableLinks = links.filter(link => link.text != 'View Post');
  function renderActionMenu() {
    const options = [ItemActions.Repost, ItemActions.Delete];
    const onDelete = () => deletePost(id).then(onDeleteClose);
    const handleOption = (option: ItemActions) => {
      switch (option) {
        case ItemActions.Delete:
          return onDeleteOpen();
        case ItemActions.Repost:
          return onRepostOpen();
      }
    };
    return <>
        <CbOptionsButton className={styles.actionMenu} contentProps={{
        width: 'fit-content'
      }} dividerColor="cloud.300" onChange={handleOption} options={options} data-sentry-element="CbOptionsButton" data-sentry-source-file="CbNewsfeedItem.tsx" />
        <CbAreYouSureModal confirmButtonVariant="danger" confirmText="Delete" denyText="Cancel" headerText="Are you sure you want to delete this post?" isOpen={isDeleteOpen} onClose={onDeleteClose} onConfirm={onDelete} data-sentry-element="CbAreYouSureModal" data-sentry-source-file="CbNewsfeedItem.tsx" />
        <RepostModal isOpen={isRepostOpen} item={item} onClose={onRepostClose} data-sentry-element="RepostModal" data-sentry-source-file="CbNewsfeedItem.tsx" />
      </>;
  }
  return <Flipped flipId={id} data-sentry-element="Flipped" data-sentry-component="CbNewsfeedItem" data-sentry-source-file="CbNewsfeedItem.tsx">
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.avatar}>
            <CbAvatar image={author.imageUrl} data-sentry-element="CbAvatar" data-sentry-source-file="CbNewsfeedItem.tsx" />
          </div>
          <span className={styles.author}>{author.name}</span>
          <span className={styles.date}>{itemAge}</span>
          {isManager && renderActionMenu()}
        </div>
        <div className={styles.info}>
          <p className={styles.text} dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(linkifyStr(text, {
            className: styles.clickableLink
          }))
        }}></p>
          {displayedFiles && <CbFileDisplay files={displayedFiles} />}
          {urlForPreview && <Box className={styles.microlinkOuterContainer}>
              <Box className={styles.microlinkInnerContainer}>
                <Microlink apiKey={process.env.MICROLINK_API_KEY} key={urlForPreview} url={urlForPreview} />
              </Box>
            </Box>}
          <div className={styles.actions}>
            {displayableLinks.map((link, index) => {
            return <NextLink href={link.url} key={index}>
                  <Box marginLeft="1rem" className={`${buttonStyles.primary} ${buttonStyles.base}`}>
                    {link.text}
                  </Box>
                </NextLink>;
          })}
          </div>
          <CbReactions itemId={id} itemReactions={reactions} itemType="NewsfeedItem" showHelperText={true} data-sentry-element="CbReactions" data-sentry-source-file="CbNewsfeedItem.tsx" />
          <Comments topComment={topComment} itemId={id} commentsCount={commentsCount} data-sentry-element="Comments" data-sentry-source-file="CbNewsfeedItem.tsx" />
        </div>
      </div>
    </Flipped>;
};
function RepostModal({
  item,
  isOpen,
  onClose
}) {
  const [selectedTeams, setSelectedTeams] = useState<AdditionalFeed[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const {
    postableTeams
  } = useContext(NewsfeedContext);
  const {
    repost
  } = useNewsfeedItem({
    newsfeedItemId: item.id
  });
  const onRepost = () => {
    setSubmitting(true);
    return repost(selectedTeams.map(team => team.value)).then(onClose).finally(() => setSubmitting(false));
  };
  return <Modal isCentered isOpen={isOpen} onClose={onClose} size="xl" data-sentry-element="Modal" data-sentry-component="RepostModal" data-sentry-source-file="CbNewsfeedItem.tsx">
      <ModalOverlay data-sentry-element="ModalOverlay" data-sentry-source-file="CbNewsfeedItem.tsx" />
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="CbNewsfeedItem.tsx">
        <ModalCloseButton data-sentry-element="ModalCloseButton" data-sentry-source-file="CbNewsfeedItem.tsx" />
        <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="CbNewsfeedItem.tsx">Repost</ModalHeader>
        <ModalBody className={styles.repostModalBody} data-sentry-element="ModalBody" data-sentry-source-file="CbNewsfeedItem.tsx">
          <p className={styles.repostExplain}>
            Select which Team feeds you would like to repost this post to:
          </p>
          <CbMultiSelect onChange={setSelectedTeams} options={postableTeams} placeholder="Select teams" data-sentry-element="CbMultiSelect" data-sentry-source-file="CbNewsfeedItem.tsx" />
        </ModalBody>
        <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="CbNewsfeedItem.tsx">
          <CbButton onClick={onClose} variant="secondary" data-sentry-element="CbButton" data-sentry-source-file="CbNewsfeedItem.tsx">
            Cancel
          </CbButton>
          <CbButton isDisabled={!selectedTeams.length || submitting} onClick={onRepost} data-sentry-element="CbButton" data-sentry-source-file="CbNewsfeedItem.tsx">
            Repost
          </CbButton>
        </ModalFooter>
      </ModalContent>
    </Modal>;
}